import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Link } from 'react-router-dom';
import './auth.css';
import logo from '../../assets/logo.png';
import api from '../../utils/api';

const Login = () => {
  const [credentials, setCredentials] = useState({
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingSettings, setIsLoadingSettings] = useState(true);
  const [loginPageSettings, setLoginPageSettings] = useState({
    companyName: '',
    loginPageTitle: 'Welcome Back!',
    companyLogoUrl: logo
  });
  
  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchLoginSettings = async () => {
      setIsLoadingSettings(true);
      try {
        const response = await api.get('/api/settings/login-page');
        
        setLoginPageSettings({
          companyName: response.data.data?.companyName ?? '',
          loginPageTitle: response.data.data?.loginPageTitle ?? 'Welcome Back!',
          companyLogoUrl: response.data.data?.companyLogoUrl || logo
        });
      } catch (err) {
        setLoginPageSettings({
          companyName: '',
          loginPageTitle: 'Welcome Back!',
          companyLogoUrl: logo
        });
      } finally {
        setIsLoadingSettings(false);
      }
    };

    fetchLoginSettings();

    const handleSettingsUpdate = () => {
      fetchLoginSettings();
    };

    window.addEventListener('settingsUpdated', handleSettingsUpdate);
    
    return () => {
      window.removeEventListener('settingsUpdated', handleSettingsUpdate);
    };
  }, []);

  useEffect(() => {
    // Check if user was redirected due to session expiry
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('session_expired') === '1') {
      setError('Your session has expired. Please login again.');
      // Remove the parameter from URL without refreshing
      const newUrl = window.location.pathname;
      window.history.replaceState({}, document.title, newUrl);
    }
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCredentials(prev => ({ ...prev, [name]: value.trim() }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!credentials.email.trim() || !credentials.password.trim()) {
      setError('Please enter both email and password');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const user = await login(credentials.email.trim(), credentials.password);
      
      if (!user) {
        throw new Error('No user data returned');
      }
      
      // navigate('/dashboard');
    } catch (err) {
      let errorMessage = 'Login failed. Please check your credentials.';
      
      if (err.response) {
        if (err.response.status === 400) {
          errorMessage = err.response.data.message || 'Invalid email or password format';
        } else if (err.response.status === 401) {
          errorMessage = 'Invalid email or password';
        } else if (err.response.status === 403) {
          errorMessage = 'Account is inactive. Please contact support.';
        }
      }

      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="auth-container">
      {/* Wavy Water Background */}
      <div className="auth-background">
        <div className="waves-container">
          <div className="wave wave-front"></div>
          <div className="wave wave-middle"></div>
          <div className="wave wave-back"></div>
        </div>
        <div className="bubbles">
          <div className="bubble"></div>
          <div className="bubble"></div>
          <div className="bubble"></div>
          <div className="bubble"></div>
          <div className="bubble"></div>
          <div className="bubble"></div>
          <div className="bubble"></div>
        </div>
      </div>
      
      {/* 3D Card Container - Front Layer */}
      <div className="auth-card-container">
        {/* Left Pane - Welcome/Branding */}
        <div className="auth-welcome-pane">
          <div className="welcome-content">
            <div className="welcome-logo">
              <img 
                src={loginPageSettings.companyLogoUrl || logo}
                alt={loginPageSettings.companyName ? `${loginPageSettings.companyName} Logo` : "Company Logo"}
                onError={(e) => {
                  e.target.src = logo;
                }}
              />
            </div>
            
            <h1 className="welcome-title">
              {loginPageSettings.companyName?.trim() || 'YongPeng Solutions Sdn. Bhd.'}
            </h1>
            
            <div className="welcome-slogan">Your Problem, Our Commitment</div>
            
            <p className="welcome-description">
              Providing cutting-edge Dell product solutions, OpenText AppEnhancer 
              Document Management Systems, and custom development services to 
              transform your business operations.
            </p>
            
            <div className="tech-highlights">
              <div className="tech-badge">Dell Solutions</div>
              <div className="tech-badge">OpenText DMS</div>
              <div className="tech-badge">Custom Development</div>
            </div>
          </div>
        </div>

        {/* Right Pane - Login Form */}
        <div className="auth-login-pane">
          <div className="auth-card">
            {isLoadingSettings ? (
              <div className="loading-overlay">
                <div className="loading-spinner"></div>
              </div>
            ) : (
              <>
                <div className="auth-header">
                  <h2 className="login-title">
                    {loginPageSettings.loginPageTitle?.trim() || 'Welcome Back!'}
                  </h2>
                  
                  <p className="login-prompt">Please login to your account</p>
                </div>

                {error && (
                  <div className="error-message" role="alert">
                    <div className="error-main">{error}</div>
                    {error.includes('check your credentials') && (
                      <div className="error-tip">
                        Tip: Make sure your email and password are correct
                      </div>
                    )}
                  </div>
                )}

                <form onSubmit={handleSubmit} noValidate>
                  <div className="form-group">
                    <label htmlFor="email">Email Address</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={credentials.email}
                      onChange={handleChange}
                      placeholder="Enter your email address"
                      required
                      autoFocus
                      autoComplete="username"
                      disabled={isLoading}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="password">Password</label>
                    <input
                      type="password"
                      id="password"
                      name="password"
                      value={credentials.password}
                      onChange={handleChange}
                      placeholder="Enter your password"
                      required
                      autoComplete="current-password"
                      disabled={isLoading}
                    />
                  </div>

                  <div className="form-options">
                    <div className="remember-me">
                      <input 
                        type="checkbox" 
                        id="remember" 
                        disabled={isLoading}
                      />
                      <label htmlFor="remember">Remember me</label>
                    </div>
                    <Link 
                      to="/forgot-password" 
                      className="forgot-password"
                      aria-disabled={isLoading}
                    >
                      Forgot password?
                    </Link>
                  </div>

                  <button 
                    type="submit" 
                    className={`login-button ${isLoading ? 'loading' : ''}`}
                    disabled={isLoading}
                    aria-busy={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <span className="spinner" aria-hidden="true"></span>
                        <span className="sr-only">Signing in...</span>
                      </>
                    ) : 'SIGN IN'}
                  </button>
                </form>

                <div className="login-footer">
                  <p>
                    Don't have an account?{' '}
                    <Link 
                      to="/contact-administrator" 
                      className="contact-admin"
                      aria-disabled={isLoading}
                    >
                      Request Access
                    </Link>
                  </p>
                </div>

                <div className="website-url">https://www.yps.com.my</div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;